/**
 * Accounting dates are calendar days (YYYY-MM-DD) in the company/local calendar.
 * They are never taken from toISOString(), which uses UTC and can move the day backward.
 */

export function localCalendarDate(date: Date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

declare global {
  interface Date {
    toDocumentDate(): string
  }
}

export function installDocumentDates(): void {
  if (Date.prototype.toDocumentDate) return
  Date.prototype.toDocumentDate = function toDocumentDate(this: Date) {
    return localCalendarDate(this)
  }
}

installDocumentDates()

/**
 * Convert a Gregorian date to Hijri date
 */
export function gregorianToHijri(date: Date): { year: number; month: number; day: number } {
  try {
    const formatter = new Intl.DateTimeFormat('en-SA-u-ca-islamic', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      calendar: 'islamic'
    });

    const parts = formatter.formatToParts(date);
    const year = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
    const month = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10);
    const day = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);

    return { year, month, day };
  } catch (error) {
    console.error('Error converting Gregorian to Hijri:', error);
    // Fallback approximation
    const hijriYear = Math.floor((date.getFullYear() - 622) * 1.0307);
    const hijriMonth = date.getMonth() + 1;
    const hijriDay = date.getDate();
    return { year: hijriYear, month: hijriMonth, day: hijriDay };
  }
}

/**
 * Convert a Hijri date to Gregorian date
 */
export function hijriToGregorian(year: number, month: number, day: number): Date {
  try {
    // Create a date string in ISO format for the Hijri date
    // We'll use a workaround with Intl.DateTimeFormat
    // First, create an approximate Gregorian date
    const approximateGregorianYear = Math.floor(year * 0.97) + 622;
    const approximateDate = new Date(approximateGregorianYear, month - 1, day);

    // Use Intl to get the exact Hijri date for this Gregorian date
    // Then adjust to get the correct Gregorian date
    // Binary search for the correct date
    let low = new Date(approximateGregorianYear - 50, 0, 1);
    let high = new Date(approximateGregorianYear + 50, 11, 31);
    let bestDate = approximateDate;

    for (let i = 0; i < 20; i++) {
      const mid = new Date((low.getTime() + high.getTime()) / 2);
      const hijri = gregorianToHijri(mid);
      
      if (hijri.year === year && hijri.month === month && hijri.day === day) {
        bestDate = mid;
        break;
      } else if (hijri.year < year || (hijri.year === year && hijri.month < month) || 
                 (hijri.year === year && hijri.month === month && hijri.day < day)) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return bestDate;
  } catch (error) {
    console.error('Error converting Hijri to Gregorian:', error);
    // Fallback approximation
    const gregorianYear = Math.floor(year * 0.97) + 622;
    return new Date(gregorianYear, month - 1, day);
  }
}

/**
 * Extract YYYY-MM-DD from API/DB values without timezone shift.
 */
export function extractDateOnlyString(value: string): string | null {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Format a date string (YYYY-MM-DD) to display format based on calendar type
 */
export function formatDateString(dateString: string, calendarType: 'gregorian' | 'hijri', format: string): string {
  if (!dateString) return '';
  
  const date = new Date(dateString + 'T00:00:00Z'); // Parse as UTC
  if (isNaN(date.getTime())) return '';

  if (calendarType === 'hijri') {
    const hijri = gregorianToHijri(date);
    return format
      .replace('dd', hijri.day.toString().padStart(2, '0'))
      .replace('MM', hijri.month.toString().padStart(2, '0'))
      .replace('yyyy', hijri.year.toString())
      .replace('yy', hijri.year.toString().slice(-2));
  } else {
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString();
    return format
      .replace('dd', day)
      .replace('MM', month)
      .replace('yyyy', year)
      .replace('yy', year.slice(-2));
  }
}

/**
 * Parse a date string from display format to UTC Gregorian date string (YYYY-MM-DD)
 */
export function parseDateString(dateString: string, calendarType: 'gregorian' | 'hijri', format: string): string {
  if (!dateString) return '';

  const parts = dateString.split(/[\/\-\.]/);
  if (parts.length !== 3) return '';

  let day: number, month: number, year: number;

  // Determine order based on format
  if (format.startsWith('dd')) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else if (format.startsWith('MM')) {
    month = parseInt(parts[0], 10);
    day = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else {
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) return '';

  let gregorianDate: Date;
  if (calendarType === 'hijri') {
    gregorianDate = hijriToGregorian(year, month, day);
  } else {
    gregorianDate = new Date(Date.UTC(year, month - 1, day));
  }

  // Return as YYYY-MM-DD in UTC
  const yearStr = gregorianDate.getUTCFullYear().toString();
  const monthStr = (gregorianDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const dayStr = gregorianDate.getUTCDate().toString().padStart(2, '0');
  
  return `${yearStr}-${monthStr}-${dayStr}`;
}

/**
 * Calendar day of a Date in local time, sent to the API as YYYY-MM-DD.
 */
export function dateToUTCString(date: Date | null | undefined): string {
  if (!date) return '';
  if (isNaN(date.getTime())) return '';
  return localCalendarDate(date);
}

/**
 * Convert UTC date string (YYYY-MM-DD) to Date object
 */
export function utcStringToDate(dateString: string): Date | null {
  if (!dateString) return null;
  
  // Handle both YYYY-MM-DD and ISO format
  const date = new Date(dateString + 'T00:00:00Z');
  if (isNaN(date.getTime())) return null;
  
  return date;
}

